import Link from 'next/link'
import React, { useState, useMemo, useCallback } from 'react'

import ZTeamMemberCard from '@/_components/custom/universal/team-member-card/z-team-member-card'

import { ITeamCategoryGridProps } from '../_types/team-categories.types'

// Konštanty pre lepšiu maintainability
const INITIAL_VISIBLE_COUNT = 3

const ATeamCategoryGrid: React.FC<ITeamCategoryGridProps> = ({
	employees,
	hasEmployees,
	categorySlug
}) => {
	const [showAll, setShowAll] = useState(false)

	// Memoizované hodnoty pre lepší výkon
	const displayEmployees = useMemo(
		() => (showAll ? employees : employees.slice(0, INITIAL_VISIBLE_COUNT)),
		[employees, showAll]
	)
	const hasMoreEmployees = useMemo(
		() => employees.length > INITIAL_VISIBLE_COUNT,
		[employees.length]
	)

	const handleScrollToLastVisibleCard = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
		try {
			const targetElement = e.target as HTMLAnchorElement
			const teamCategoryGrid = targetElement.closest('.team-category-grid')

			if (teamCategoryGrid) {
				// Find the mobile container (contents md:hidden)
				const mobileContainer = teamCategoryGrid.querySelector('.contents.md\\:hidden')

				if (mobileContainer) {
					// Get the last visible card (INITIAL_VISIBLE_COUNT - 1, which is index 2 for 3 cards)
					const lastVisibleCard = mobileContainer.children[
						INITIAL_VISIBLE_COUNT - 1
					] as HTMLElement

					if (lastVisibleCard) {
						window.scrollTo({
							top: lastVisibleCard.getBoundingClientRect().top + window.scrollY - 150,
							behavior: 'instant'
						})
					}
				}
			}
		} catch {
			return
		}
	}, [])

	// Callback funkcia pre toggle
	const handleToggle = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, value: boolean) => {
			e.preventDefault()

			if (!value && typeof window !== 'undefined') {
				handleScrollToLastVisibleCard(e)
			}

			setShowAll(value)
		},
		[handleScrollToLastVisibleCard]
	)

	// Render funkcia pre kartičku zamestnanca
	const renderEmployeeCard = useCallback(
		(employee: any) => (
			<ZTeamMemberCard
				key={`${categorySlug}-${employee.id}`}
				employee={employee}
				reservationButton={false}
			/>
		),
		[categorySlug]
	)

	// Render funkcia pre toggle tlačidlo
	const renderToggleButton = useCallback(
		() => (
			<Link
				href='#'
				onClick={e => handleToggle(e, !showAll)}
				className='px-6 py-3 text-white transition-all rounded-full bg-medante-purple-dark hover:bg-purple-900'
				aria-label={showAll ? 'Zobraziť menej zamestnancov' : 'Zobraziť viac zamestnancov'}
			>
				{showAll ? 'Zobraziť menej' : 'Zobraziť viac'}
			</Link>
		),
		[handleToggle, showAll]
	)

	if (!employees || employees.length === 0) {
		return (
			<div className='text-center text-gray-500 pt-4'>
				{!hasEmployees
					? 'V tejto kategórii sa nenachádzajú žiadny členovia'
					: 'Nenašli sa žiadni členovia'}
			</div>
		)
	}

	return (
		<div className='space-y-6 team-category-grid'>
			<div className='grid grid-cols-1 gap-4 py-12 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
				{/* Mobilné zariadenia - zobrazia sa iba prvé kartičky */}
				<div className='contents md:hidden'>{displayEmployees.map(renderEmployeeCard)}</div>

				{/* Desktop zariadenia - zobrazia sa všetky kartičky */}
				<div className='hidden md:contents'>{employees.map(renderEmployeeCard)}</div>
			</div>

			{/* Tlačidlo iba na mobilnom zariadení */}
			{hasMoreEmployees && (
				<div className='flex justify-center pb-8 md:hidden'>{renderToggleButton()}</div>
			)}
		</div>
	)
}

export default ATeamCategoryGrid
